/**
 * Cadastro e edição de cliente — rotas `/app/clientes/novo` e
 * `/app/clientes/:codigo/editar`.
 *
 * ## Por que virou rota
 *
 * Isto vivia DENTRO de `TelaClientes`, como um `useState` de três modos
 * (lista, cadastro, edição). A justificativa escrita era que o protótipo mantém
 * "Clientes" aceso na barra lateral — só que uma rota aninhada faz isso do
 * mesmo jeito, e o estado cobrava quatro preços:
 *
 *  1. F5 no meio do cadastro descartava dezoito campos preenchidos;
 *  2. o Voltar do navegador saía da tela de clientes inteira, não do
 *     formulário;
 *  3. não havia como mandar "abre a edição desse cliente" para alguém;
 *  4. foi preciso escrever uma gambiarra com `location.key` só para o clique no
 *     item do menu funcionar — sintoma clássico de estado que devia estar na
 *     URL. Ela morreu junto.
 *
 * Produtos já resolvia assim (`/app/produtos/novo`). Cliente e fornecedor agora
 * seguem o mesmo caminho.
 *
 * ## Por que continua sendo TELA, e não gaveta
 *
 * São dezoito campos em seis blocos, com rail de resumo à direita e um seletor
 * que troca os CAMPOS (PF ganha nascimento, PJ ganha razão social e inscrição
 * estadual). O protótipo `Lumma Fit Cadastro Cliente e Fornecedor.dc.html`
 * desenha exatamente isso: tela cheia com breadcrumb, Cancelar/Salvar no topo e
 * rail. Em gaveta de 480px os pares de campo quebrariam em coluna única e o
 * formulário teria o dobro da altura — e o Esc descartaria tudo sem aviso.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { ErroApi } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'
import { carregarFicha, type FichaCliente } from '../../plataforma/clientes'
import { LayoutApp } from '../app/LayoutApp'
import { FormularioCliente, ID_FORMULARIO_CLIENTE } from './FormularioCliente'

export function TelaCadastroCliente() {
  return <Formulario />
}

export function TelaEdicaoCliente() {
  const { codigo = '' } = useParams<{ codigo: string }>()
  const [ficha, setFicha] = useState<FichaCliente | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroDeCarga, setErroDeCarga] = useState('')

  useEffect(() => {
    const controle = new AbortController()
    setCarregando(true)

    carregarFicha(codigo, controle.signal)
      .then((dados) => {
        setFicha(dados)
        setErroDeCarga('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroDeCarga(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o cliente.',
        )
      })
      .finally(() => setCarregando(false))

    return () => controle.abort()
  }, [codigo])

  if (carregando) {
    return (
      <LayoutApp titulo="Editar cliente" subtitulo="Carregando…">
        <div className="h-[420px] w-full animate-pulse rounded-sistema bg-superficie" />
      </LayoutApp>
    )
  }

  if (erroDeCarga || !ficha) {
    return (
      <LayoutApp titulo="Editar cliente" subtitulo={codigo}>
        <div className="flex w-full flex-col gap-4">
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erroDeCarga || 'Cliente não encontrado.'}
          </div>
          <div>
            <LinkBotao para="/app/clientes" variante="secundario" icone="arrow_back">
              Voltar aos clientes
            </LinkBotao>
          </div>
        </div>
      </LayoutApp>
    )
  }

  return <Formulario ficha={ficha} />
}

/**
 * A moldura comum às duas rotas.
 *
 * "Salvar" mora no CABEÇALHO e é um `<button form={id}>` — o jeito nativo de um
 * botão fora do `<form>` disparar o submit dele. É o que permite a ação ficar
 * fixa no topo enquanto o formulário rola, sem duplicar o botão no rodapé.
 */
function Formulario({ ficha }: { ficha?: FichaCliente }) {
  const { pode } = useSessao()
  const navegar = useNavigate()
  const [enviando, setEnviando] = useState(false)

  const editando = Boolean(ficha)

  return (
    <LayoutApp
      largura="formulario"
      trilha={[
        { rotulo: 'Clientes', para: '/app/clientes' },
        { rotulo: editando ? (ficha?.nome ?? 'Editar') : 'Novo cliente' },
      ]}
      voltarPara="auto"
      titulo={editando ? 'Editar cliente' : 'Cadastro de cliente'}
      subtitulo={
        editando
          ? `Ficha ${ficha?.codigo ?? ''}`
          : 'Dados, endereço e preferências de quem compra na loja'
      }
      acoes={
        <div className="flex items-center gap-2.5">
          {/* `navegar(-1)` e não um link fixo para a lista: quem chegou aqui
              pela ficha aberta na gaveta espera voltar para a ficha, não para o
              topo da listagem. */}
          <Botao variante="secundario" onClick={() => navegar(-1)}>
            Cancelar
          </Botao>
          <Botao type="submit" form={ID_FORMULARIO_CLIENTE} icone="check" disabled={enviando}>
            {enviando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Salvar cliente'}
          </Botao>
        </div>
      }
    >
      <FormularioCliente
        ficha={ficha}
        podeCrediario={pode('credit.enable')}
        aoEstadoMudar={setEnviando}
        aoSalvar={() => navegar('/app/clientes')}
      />
    </LayoutApp>
  )
}
